/**
 * 报表页**纯逻辑层**（K 域 · 端侧）—— 「服务端下发形状」→「页面模型」
 *
 * 依据：K 域 PRD **§4.5.4**（三层组织）· `AC-K4.13`（数据不足 ≠ 0）· `AC-K4.16`（泛泛建议不显示）·
 * `AC-K4.17`（整层不显示由服务端决定 —— 端侧**不补空层**）。
 *
 * ## 🔴 本文件是**纯函数**（零 DOM / 零 React / 零 IO）
 *
 * 与 `txn-draft.ts` / `account-draft.ts` / `keymap.ts` 同范式：规则放这里 → 可直测；
 * 组件只做"把模型渲染成 DOM"。
 *
 * 🔴 **本文件不判口径**：
 * - 「该层显示不显示」= **服务端的 return**（`health.lines` 里没有就没有）⇒ 端侧**不补空层**；
 * - 「该指标该不该显示数字」= 服务端的 `sufficient` / `unavailable`；
 * - 端侧只做**状态归类**（三态）与**取词**。
 */

import type { CategoryNodeView, HealthLineView, HealthMetricKey, HealthMetricView } from '@jiaying/shared';
import {
  INSUFFICIENT_TEXT,
  UNAVAILABLE_PREFIX,
  adviceText,
  LAYER_QUESTIONS,
  metricText,
} from './report-labels.js';

/** 指标行的三态 —— 🔴 三态**必须区分**（"数据不足" ≠ "未就绪" ≠ 0） */
export type MetricState = 'ok' | 'insufficient' | 'unavailable';

export interface MetricRow {
  key: HealthMetricKey;
  label: string;
  /** 显示文本；非 `ok` 时为「—」 */
  text: string;
  state: MetricState;
  /** `unavailable` 时给原因（可直接展示）；其余为 `null` */
  note: string | null;
}

export interface HealthLineModel {
  layer: string;
  /** 该层回答的问题（「扛不扛得住」…） */
  question: string;
  rows: MetricRow[];
  /** 🔴 `null` = 该层**无数字支撑的动作** ⇒ 只出陈述（`AC-K4.16`） */
  advice: string | null;
}

function toRow(m: HealthMetricView): MetricRow {
  if (m.unavailable !== null) {
    return { key: m.key, label: m.label, text: '—', state: 'unavailable', note: m.unavailable.missing };
  }
  if (!m.sufficient) {
    return { key: m.key, label: m.label, text: '—', state: 'insufficient', note: null };
  }
  return { key: m.key, label: m.label, text: metricText(m), state: 'ok', note: null };
}

/** 一层 → 页面模型（顺序 = 服务端给的顺序，**不重排**） */
export function healthLineModel(line: HealthLineView, currency: string): HealthLineModel {
  return {
    layer: line.layer,
    question: LAYER_QUESTIONS[line.layer],
    rows: line.metrics.map(toRow),
    advice: line.advice === null ? null : adviceText(line.advice, currency),
  };
}

/** 三层一起转（保持服务端顺序） */
export function healthLineModels(
  lines: readonly HealthLineView[],
  currency: string,
): HealthLineModel[] {
  return lines.map((l) => healthLineModel(l, currency));
}

export { INSUFFICIENT_TEXT, UNAVAILABLE_PREFIX };

/**
 * 在**分类树**里找某个分类的金额（`AC-K2.9` 的口径对照用 —— 下钻抽屉要写「卡片金额 ¥X」）。
 *
 * 🔴 只**查找**、不计算：金额一律取自服务端下发的节点（端侧不做任何金额算术）。
 * 🔴 覆盖两级（一级节点自身 + 其子节点）—— 与服务端树的形状一一对应。
 *
 * @returns `null` = 树里没有这个 id（例如节点被删）⇒ 调用方**不提具体数字**，只说口径差异
 */
export function findCategoryAmount(
  nodes: readonly CategoryNodeView[],
  categoryId: string | null,
): number | null {
  if (categoryId === null) return null;
  for (const n of nodes) {
    if (n.categoryId === categoryId) return n.amount;
    for (const c of n.children) if (c.categoryId === categoryId) return c.amount;
  }
  return null;
}
